import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook para buscar dados completos de um item usando a view v_itens_pedido_completos
 * Inclui automaticamente produto, gestor e cessionário em uma única query
 */
export function useItemCompleto(itemId: string | null) {
  return useQuery({
    queryKey: ['item-completo', itemId],
    queryFn: async () => {
      if (!itemId) return null;

      const { data, error } = await supabase
        .from('v_itens_pedido_completos' as any)
        .select('*')
        .eq('id', itemId)
        .maybeSingle();

      if (error || !data) {
        console.error('❌ Erro ao buscar item completo:', error);
        throw error || new Error('Item não encontrado');
      }

      // ✅ NOVO: Buscar linhas_telefone, observacoes_item, campos de data e campos de agenda separadamente
      const { data: linhasData, error: linhasError } = await supabase
        .from('itens_pedido')
        .select('linhas_telefone, observacoes_item, data_ativacao, mes_trabalho, data_agendamento, status_ativacao, agenda_tipo, agenda_data, agenda_observacoes, endereco_instalacao, sem_linha_fixa, numero_cotacao_operadora, tt_contrato_pf')
        .eq('id', itemId)
        .maybeSingle();

      if (linhasError) {
        console.error('❌ Erro ao buscar linhas telefônicas:', linhasError);
      }

      // ✅ NOVO: Buscar histórico de recuperação do item
      const { data: movimentoRecuperacao } = await supabase
        .from('movimento')
        .select('id, tipo, created_at, para')
        .eq('entidade', 'pedido')
        .eq('tipo', 'recuperacao')
        .order('created_at', { ascending: false })
        .limit(100);

      // Verificar se este item específico foi recuperado
      const foiRecuperado = movimentoRecuperacao?.some(m => {
        if (!m.para || typeof m.para !== 'object') return false;
        const para = m.para as { item_id?: string };
        return para.item_id === itemId;
      }) || false;

      console.log('🔵 [useItemCompleto] Verificação de recuperação:', {
        itemId,
        foiRecuperado,
        totalMovimentosRecuperacao: movimentoRecuperacao?.length || 0
      });

      // ✅ Mesclar dados da view + linhas_telefone + observacoes_item + campos de data + campos de agenda + foi_recuperado + sem_linha_fixa
      const itemCompleto: any = Object.assign({}, data, {
        linhas_telefone: linhasData?.linhas_telefone || [],
        observacoes_item: linhasData?.observacoes_item || (data as any)?.observacoes_item,
        data_ativacao: linhasData?.data_ativacao || (data as any)?.data_ativacao,
        mes_trabalho: linhasData?.mes_trabalho || (data as any)?.mes_trabalho,
        data_agendamento: linhasData?.data_agendamento || (data as any)?.data_agendamento,
        status_ativacao: linhasData?.status_ativacao || (data as any)?.status_ativacao,
        agenda_tipo: linhasData?.agenda_tipo || (data as any)?.agenda_tipo,
        agenda_data: linhasData?.agenda_data || (data as any)?.agenda_data,
        agenda_observacoes: linhasData?.agenda_observacoes || (data as any)?.agenda_observacoes,
        endereco_instalacao: linhasData?.endereco_instalacao || (data as any)?.endereco_instalacao,
        sem_linha_fixa: linhasData?.sem_linha_fixa ?? (data as any)?.sem_linha_fixa ?? false,
        numero_cotacao_operadora: linhasData?.numero_cotacao_operadora || (data as any)?.numero_cotacao_operadora,
        tt_contrato_pf: linhasData?.tt_contrato_pf ?? null,
        foi_recuperado: foiRecuperado
      });

      // ✅ Formatar datas para o formato HTML esperado (YYYY-MM-DD)
      if (itemCompleto.data_ativacao) {
        const dataAtivacao = new Date(itemCompleto.data_ativacao);
        if (!isNaN(dataAtivacao.getTime())) {
          itemCompleto.data_ativacao = dataAtivacao.toISOString().split('T')[0];
        }
      }

      // data_agendamento fica COMPLETO: a agenda tem hora (portabilidade) ou turno
      // (instalação). Cortar para a data em UTC perdia o turno e trocava o dia
      // das agendas depois das 21h (Douglas 26/09/2026).

      console.log('✅ [useItemCompleto] Item mesclado:', {
        id: itemId,
        data_ativacao_view: (data as any)?.data_ativacao,
        data_ativacao_query: linhasData?.data_ativacao,
        data_ativacao_final: itemCompleto.data_ativacao,
        mes_trabalho_view: (data as any)?.mes_trabalho,
        mes_trabalho_query: linhasData?.mes_trabalho,
        mes_trabalho_final: itemCompleto.mes_trabalho,
        linhas_telefone: itemCompleto.linhas_telefone,
        observacoes_item: itemCompleto.observacoes_item
      });

      return itemCompleto as any;
    },
    enabled: !!itemId,
    staleTime: 0, // ✅ SEMPRE buscar dados frescos (sem cache)
  });
}
